import {
  datadogRum,
  RumEvent,
  RumFetchResourceEventDomainContext,
  RumResourceEvent,
  RumXhrResourceEventDomainContext,
} from '@datadog/browser-rum';
import {
  CustomRumEvent,
  CustomRumEventData,
  Service,
  ZDOCS_REQUEST_ID_HEADER,
} from './constants';
import logger from './logging/logger';
import { redactTokenFromPayload, redactTokenPayload, TokenInfo } from './token';
import { window } from './utils/browser';

function getResourceEvent(event: RumEvent) {
  return event.type === 'resource' ? (event as RumResourceEvent) : undefined;
}

function isFetchResourceEventType(resourceEvent: RumResourceEvent) {
  return resourceEvent?.resource.type === 'fetch';
}

function isXhrResourceEventType(resourceEvent: RumResourceEvent) {
  return resourceEvent?.resource.type === 'xhr';
}

function getResourceEventStatusCode(resourceEvent?: RumResourceEvent) {
  return resourceEvent?.resource.status_code ?? 0;
}

function redactContentFromEventContext(
  resourceEvent: RumResourceEvent,
  context: any
) {
  if (isFetchResourceEventType(resourceEvent)) {
    const { requestInit } = context as RumFetchResourceEventDomainContext;

    if (requestInit) {
      delete requestInit.body;
    }
  }
}

function getZDocsRequestId(resourceEvent: RumResourceEvent, context: any) {
  if (isFetchResourceEventType(resourceEvent)) {
    const { headers } =
      (context as RumFetchResourceEventDomainContext).response ?? {};

    return headers?.get(ZDOCS_REQUEST_ID_HEADER) ?? null;
  }

  if (isXhrResourceEventType(resourceEvent)) {
    const { xhr } = (context as RumXhrResourceEventDomainContext) ?? {};

    return xhr?.getResponseHeader(ZDOCS_REQUEST_ID_HEADER) ?? null;
  }

  return null;
}

function getRumEventContext(event: RumEvent, zDocsRequestId: string | null) {
  if (zDocsRequestId) {
    return {
      ...event.context,
      zDocsRequestId,
    };
  }

  return event.context;
}

const isDeployed =
  window?.location.hostname.endsWith('zillowdocs.com') ?? false;

export function rumInit(service: Service, tokenInfo?: TokenInfo) {
  if (isDeployed && window) {
    try {
      const env =
        import.meta.env.DEV ||
        window.location.hostname === 'localhost' ||
        window.location.hostname.endsWith('dev-zillowdocs.com')
          ? 'dev'
          : window.location.hostname.endsWith('qa-zillowdocs.com')
          ? 'qa'
          : 'prod';
      // TODO: Resolve lint error with internal imports of rum not acknowledging Window interface defined in global.d.ts
      const version = (window as any).VERSION;

      datadogRum.init({
        applicationId: '424d7bb6-95af-4ec8-b9b6-a2e517ebe9f6',
        clientToken: 'pub57ecb1dde21bcc7b02285453acf30643',
        site: 'datadoghq.com',
        service,
        env,
        version,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        trackUserInteractions: false,
        trackResources: true,
        usePartitionedCrossSiteSessionCookie: true,
        allowedTracingUrls: [/^https:\/\/(www\.)?(dev-|qa-)?zillowdocs\.com$/],
        beforeSend(event, context) {
          const resourceEvent = getResourceEvent(event);
          const statusCode = getResourceEventStatusCode(resourceEvent);
          const isErrorEvent = resourceEvent && statusCode >= 400;

          if (resourceEvent) {
            redactContentFromEventContext(resourceEvent, context);
          }

          redactTokenFromPayload(event, tokenInfo);

          if (isErrorEvent) {
            const zDocsRequestId = getZDocsRequestId(resourceEvent, context);
            event.context = getRumEventContext(event, zDocsRequestId);
            datadogRum.addError(
              `(${statusCode}) Error making API request to ${resourceEvent.resource.url}`,
              context
            );
          }
          return true;
        },
      });

      if (tokenInfo?.payload) {
        const { payload: tokenPayload } = tokenInfo;
        const user: { id: string; name?: string } = {
          id: tokenPayload.sub,
        };

        if (tokenPayload.sub.startsWith('admin/')) {
          user.name = tokenPayload.zdocs.name;
        }

        datadogRum.setUser(user);
      }

      const redactedTokenPayload = tokenInfo?.payload
        ? redactTokenPayload(tokenInfo.payload)
        : {};

      setRumContextProperty('tokenPayload', redactedTokenPayload);
    } catch (e) {
      logger.error('Failed to initialize DataDog RUM');
    }
  }
}

export function customRumEvent({ actionName, meta }: CustomRumEventData) {
  switch (actionName) {
    case CustomRumEvent.SdkVersion: {
      datadogRum.addAction(actionName, { version: meta });
      break;
    }
  }
}

export function sendSdkVersionRumEvent(version: string) {
  customRumEvent({
    actionName: CustomRumEvent.SdkVersion,
    meta: version,
  });
}

export function setRumContextProperty(key: string, value: any) {
  datadogRum.setGlobalContextProperty(key, value);
}
